import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Target, FlaskConical, ChevronRight, RotateCcw } from 'lucide-react'
import { SectionGrid } from '../SectionGrid'
import { PrerequisiteWarning } from '../PrerequisiteWarning'
import { exercisesAPI } from '../../../api/exercises'
import { useCoursePrerequisites } from '../../../hooks/useCoursePrerequisites'
import { isExerciseComplete, type PendingPrerequisite } from '../../../lib/prerequisites'
import type { ExerciseDetail } from '../../../types/api'

interface ExerciseWithProgress extends ExerciseDetail {
  progress: number
  status: string
  tasks_completed: number
  total_tasks: number
}

const getExerciseIcon = (exercise: ExerciseWithProgress) => {
  const icons = [Target, FlaskConical]
  return icons[exercise.id % icons.length]
}

interface ExerciseCardProps {
  exercise: ExerciseWithProgress
  index: number
  isResetting: boolean
  onClick: () => void
  onReset: (event: React.MouseEvent) => void
}

const ExerciseCard = ({
  exercise,
  index,
  isResetting,
  onClick,
  onReset
}: ExerciseCardProps): React.JSX.Element => {
  const progress = exercise.progress ?? 0
  const isStarted = exercise.status !== 'not_started'
  const isCompleted = exercise.status === 'completed' || progress >= 100
  const taskCount = exercise.total_tasks || exercise.tasks?.length || 0
  const Icon = getExerciseIcon(exercise)

  const blurb =
    exercise.objectives?.[0]?.text ||
    'Work through this hands-on lab exercise with guidance from your instructor.'

  return (
    <div
      className="flex-1 min-w-[280px] max-w-[320px] bg-white rounded-lg shadow-md p-5 border border-slate-200 border-l-4 border-l-[#7a6020] transition-all duration-300 relative hover:shadow-lg hover:scale-[1.02] cursor-pointer"
      onClick={onClick}
    >
      {isStarted && (
        <button
          onClick={onReset}
          disabled={isResetting}
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
          title="Reset exercise progress"
        >
          <RotateCcw
            className={`w-4 h-4 text-gray-600 group-hover:text-black transition-colors ${
              isResetting ? 'animate-spin' : ''
            }`}
          />
        </button>
      )}

      <div className="flex items-center gap-2.5 mb-3 pb-2.5 border-b border-slate-200 pr-8">
        <Icon className="size-6 text-black" strokeWidth={2} />
        <h2 className="text-lg font-bold text-black">{exercise.title}</h2>
      </div>

      <div className="text-sm text-gray-700 leading-relaxed mb-3">{blurb}</div>

      {isStarted && (
        <div className="mb-3">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs text-gray-600">Progress</span>
            <span className="text-xs font-medium text-black">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-black h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      <div className="pt-3 border-t border-slate-200 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          Exercise {exercise.order_index ?? index + 1}
        </span>
        {taskCount > 0 && (
          <span className="text-xs text-gray-500">
            {exercise.tasks_completed ?? 0}/{taskCount} tasks
          </span>
        )}
      </div>

      {isCompleted ? (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onClick()
          }}
          className="mt-3 w-full flex items-center justify-center gap-1.5 py-2.5 px-4 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Review Exercise <ChevronRight className="w-4 h-4" />
        </button>
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onClick()
          }}
          className="mt-3 w-full flex items-center justify-center gap-1.5 py-2.5 px-4 rounded-lg text-sm font-semibold text-white transition-colors hover:opacity-90"
          style={{ background: '#7a6020' }}
        >
          {isStarted ? 'Resume Exercise' : 'Start Exercise'}
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}

interface AvailableExercisesProps {
  moduleId: string | undefined
  courseId?: string
  onExercisesLoaded?: (exercises: ExerciseWithProgress[]) => void
}

interface PendingWarning {
  exercise: ExerciseWithProgress
  prerequisites: PendingPrerequisite[]
}

export const AvailableExercises: React.FC<AvailableExercisesProps> = ({
  moduleId,
  courseId,
  onExercisesLoaded
}) => {
  const navigate = useNavigate()
  const [exercises, setExercises] = useState<ExerciseWithProgress[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [resetting, setResetting] = useState<Set<number>>(new Set())
  const [warning, setWarning] = useState<PendingWarning | null>(null)

  const { getPendingPrerequisites } = useCoursePrerequisites({
    courseId,
    moduleId,
    moduleExercises: exercises
  })

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (!moduleId) {
        setLoading(false)
        return
      }
      try {
        const data = await exercisesAPI.getModuleExercises(moduleId)
        setExercises(data)
        onExercisesLoaded?.(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [moduleId])

  const openExercise = (exerciseId: number): void => {
    setWarning(null)
    navigate(`/exercise/${exerciseId}/chat`)
  }

  /**
   * Warn before opening an exercise that has unfinished exercises ahead of it
   * in the course — the learner can still continue if they want to.
   */
  const handleExerciseClick = (exercise: ExerciseWithProgress): void => {
    if (isExerciseComplete(exercise)) {
      openExercise(exercise.id)
      return
    }
    const prerequisites = getPendingPrerequisites(exercise.id)
    if (prerequisites.length === 0) {
      openExercise(exercise.id)
      return
    }
    setWarning({ exercise, prerequisites })
  }

  const handleReset = async (
    exerciseId: number,
    event: React.MouseEvent
  ): Promise<void> => {
    event.stopPropagation()

    setResetting((prev) => new Set(prev).add(exerciseId))
    try {
      await exercisesAPI.resetExercise(exerciseId)
      setExercises((prev) => {
        const next = prev.map((ex) =>
          ex.id === exerciseId
            ? {
                ...ex,
                status: 'not_started',
                progress: 0,
                tasks_completed: 0
              }
            : ex
        )
        onExercisesLoaded?.(next)
        return next
      })
    } catch (err) {
      console.error('Error resetting exercise:', err)
      alert('Failed to reset exercise progress. Please try again.')
    } finally {
      setResetting((prev) => {
        const next = new Set(prev)
        next.delete(exerciseId)
        return next
      })
    }
  }

  return (
    <>
      <SectionGrid
        title="Available Exercises"
        subtitle="Put your knowledge to the test with hands-on lab exercises."
        loading={loading}
        error={error}
        emptyText="No exercises found for this module."
        loadingText="Loading exercises..."
        gridClassName="flex w-full gap-4 items-stretch flex-wrap"
      >
        {exercises.map((exercise, idx) => (
          <ExerciseCard
            key={exercise.id}
            exercise={exercise}
            index={idx}
            isResetting={resetting.has(exercise.id)}
            onClick={() => handleExerciseClick(exercise)}
            onReset={(e) => handleReset(exercise.id, e)}
          />
        ))}
      </SectionGrid>

      {warning && (
        <PrerequisiteWarning
          exerciseTitle={warning.exercise.title}
          prerequisites={warning.prerequisites}
          onGoToPrerequisite={openExercise}
          onContinueAnyway={() => openExercise(warning.exercise.id)}
          onClose={() => setWarning(null)}
        />
      )}
    </>
  )
}
